/**
 * Finds `[data-ffs-form]` / `[data-ffs-reschedule]` elements (and, on custom confirmation pages,
 * `[data-ffs-page]` / `[data-ffs-block]`), attaches an open shadow root to each and renders the
 * app inside it. Runs at load and again for nodes added later
 * (MutationObserver), so page builders that inject the div after the script still work.
 */
import { render } from 'preact';
import { App } from './components/App';
import { BlockApp } from './components/Blocks';
import { RescheduleApp } from './components/RescheduleApp';
import { resolveBase } from './lib/base';
import { readOptions } from './lib/options';
import { fillPage } from './lib/page';
import css from './styles.css?inline';

// Custom confirmation pages add `[data-ffs-page]` (the page root, whose merge tags get filled in
// place) and `[data-ffs-block]` (the widgets placed with block tags, each in a shadow root).
const SELECTOR = '[data-ffs-form],[data-ffs-reschedule],[data-ffs-page],[data-ffs-block]';
const MOUNTED = 'data-ffs-mounted';
const mounted = new WeakSet<Element>();

function mountOne(host: HTMLElement): void {
  if (mounted.has(host) || host.hasAttribute(MOUNTED)) return;
  mounted.add(host);
  host.setAttribute(MOUNTED, '');
  const base = resolveBase(host);
  if (host.hasAttribute('data-ffs-page')) {
    void fillPage(host, base);
    return;
  }
  const block = host.getAttribute('data-ffs-block');
  if (block) {
    const page = host.closest('[data-ffs-page]');
    const token = page?.getAttribute('data-ffs-registered')?.trim() || null;
    const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    shadow.textContent = '';
    const style = document.createElement('style');
    style.textContent = css;
    shadow.appendChild(style);
    const root = document.createElement('div');
    shadow.appendChild(root);
    render(<BlockApp base={base} name={block} token={token} />, root);
    return;
  }
  const o = readOptions(host, base);
  // The hosted reschedule page cannot set data-ffs-source itself; same-origin reschedule mounts
  // are treated as hosted so the card sits flat inside the page's own card.
  if (o.rescheduleToken && !o.hosted && typeof location !== 'undefined' && base === location.origin)
    o.hosted = true;
  const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
  shadow.textContent = '';
  const style = document.createElement('style');
  style.textContent = css;
  shadow.appendChild(style);
  const root = document.createElement('div');
  shadow.appendChild(root);
  if (o.rescheduleToken) render(<RescheduleApp o={o} token={o.rescheduleToken} />, root);
  else if (o.publicId || o.registeredToken) render(<App o={o} />, root);
}

/** Mounts every not-yet-mounted embed under `root` (default: the whole document). */
export function mountAll(root: ParentNode = document): number {
  let n = 0;
  for (const el of root.querySelectorAll<HTMLElement>(SELECTOR)) {
    if (!mounted.has(el) && !el.hasAttribute(MOUNTED)) {
      n++;
      mountOne(el);
    }
  }
  return n;
}

let observing = false;

export function start(): void {
  if (typeof document === 'undefined' || observing) return;
  observing = true;
  mountAll();
  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', () => mountAll(), { once: true });
  let pending = false;
  const schedule = () => {
    if (pending) return;
    pending = true;
    setTimeout(() => {
      pending = false;
      mountAll();
    }, 0);
  };
  new MutationObserver((records) => {
    for (const r of records) {
      if (r.addedNodes.length) {
        schedule();
        return;
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
}
